import React from 'react';
import { Card, Heading, Table, Strong, Text, Pane, Button } from 'evergreen-ui';
import { CSVLink } from "react-csv";
import { formatCurrency } from '../../../util';
import { generateDashboardPDF } from '../utils/generateDashboardPDF';

const FinancialSummaryCard = ({ revenueData, expenseData, timePeriod, formatCurrencyWithSymbol, currencySymbol, startDate, endDate }) => {
    // Calculate key metrics
    const grossRevenue = revenueData?.reduce((sum, item) => sum + item.total_amount, 0) || 0;
    const totalExpenses = expenseData?.reduce((sum, item) => sum + item.total_amount, 0) || 0;
    const netProfit = grossRevenue - totalExpenses;
    const netProfitMargin = grossRevenue ? (netProfit / grossRevenue * 100) : 0;

    // Calculate additional metrics
    const operatingMargin = grossRevenue ? ((grossRevenue - totalExpenses) / grossRevenue * 100) : 0;
    const expenseRatio = grossRevenue ? (totalExpenses / grossRevenue * 100) : 0;

    const getCSVData = () => {
        // Format the time period for display in the CSV
        const displayTimePeriod = startDate && endDate
            ? `${startDate} to ${endDate}`
            : timePeriod;

        const csvData = [
            ['Financial Summary Report', ''],
            ['Time Period:', `${displayTimePeriod}`],
            [''],
            ['Revenue Breakdown by Format'],
            ['Format', 'Quantity Sold', 'Revenue'],
            ...(revenueData?.map(item => [
                item.format,
                item.units_sold,
                `${formatCurrencyWithSymbol(item.total_amount)}`
            ]) || []),
            [''],
            ['Expense Breakdown by Category'],
            ['Category', 'Amount'],
            ...(expenseData?.map(item => [
                item.category,
                `${formatCurrencyWithSymbol(item.total_amount)}`
            ]) || []),
            [''],
            ['Summary'],
            ['Gross Revenue', '', `${formatCurrencyWithSymbol(grossRevenue)}`],
            ['Total Expenses', '', `${formatCurrencyWithSymbol(totalExpenses)}`],
            ['Net Profit', '', `${formatCurrencyWithSymbol(netProfit)}`],
            ['Net Profit Margin', '', `${netProfitMargin.toFixed(1)}%`],
            ['Operating Margin', '', `${operatingMargin.toFixed(1)}%`],
            ['Expense Ratio', '', `${expenseRatio.toFixed(1)}%`],
        ];

        return csvData;
    };

    // Generate filename for CSV download
    const csvFilename = startDate && endDate
        ? `financial_summary_${startDate}_to_${endDate}.csv`
        : `financial_summary_${String(timePeriod).toLowerCase().replace(/\s/g, '_')}.csv`;

    return (
        <>
            <Card flex={1} padding={16} marginBottom={16} background="tint1" elevation={1} marginTop={24}>
                <Heading size={600} marginBottom={16}>Financial Summary</Heading>
                <Table>
                    <Table.Body>
                        {/* Revenue Section */}
                        <Table.Row>
                            <Table.TextCell><Strong>Gross Revenue</Strong></Table.TextCell>
                            <Table.TextCell textAlign="right">
                                <Text>{formatCurrencyWithSymbol(grossRevenue)}</Text>
                            </Table.TextCell>
                            <Table.TextCell textAlign="right">100%</Table.TextCell>
                        </Table.Row>

                        {/* Expenses Section */}
                        <Table.Row intent="danger">
                            <Table.TextCell><Strong>Total Expenses</Strong></Table.TextCell>
                            <Table.TextCell textAlign="right">
                                <Text>{formatCurrencyWithSymbol(totalExpenses)}</Text>
                            </Table.TextCell>
                            <Table.TextCell textAlign="right">
                                {expenseRatio.toFixed(1)}%
                            </Table.TextCell>
                        </Table.Row>

                        {/* Net Profit Section */}
                        <Table.Row intent={netProfit >= 0 ? "success" : "danger"}>
                            <Table.TextCell><Strong>Net Profit</Strong></Table.TextCell>
                            <Table.TextCell textAlign="right">
                                <Strong>{formatCurrencyWithSymbol(netProfit)}</Strong>
                            </Table.TextCell>
                            <Table.TextCell textAlign="right">
                                <Strong>{netProfitMargin.toFixed(1)}%</Strong>
                            </Table.TextCell>
                        </Table.Row>

                        {/* Additional Metrics */}
                        <Table.Row>
                            <Table.TextCell><Strong>Operating Margin</Strong></Table.TextCell>
                            <Table.TextCell textAlign="right">
                                {operatingMargin.toFixed(1)}%
                            </Table.TextCell>
                            <Table.TextCell>
                                <Text color="muted" size={300}>
                                    Operating profit as a percentage of revenue
                                </Text>
                            </Table.TextCell>
                        </Table.Row>

                        <Table.Row>
                            <Table.TextCell><Strong>Expense Ratio</Strong></Table.TextCell>
                            <Table.TextCell textAlign="right">
                                {expenseRatio.toFixed(1)}%
                            </Table.TextCell>
                            <Table.TextCell>
                                <Text color="muted" size={300}>
                                    Total expenses as a percentage of revenue
                                </Text>
                            </Table.TextCell>
                        </Table.Row>
                    </Table.Body>
                </Table>

                {/* Export Buttons */}
                <Pane display="flex" justifyContent="flex-end" marginBottom={50} marginTop={16} gap={8}>
                    <Button
                        appearance="primary"
                        onClick={() => generateDashboardPDF({
                            revenueData,
                            expenseData,
                            summaryData: {
                                grossRevenue,
                                totalExpenses,
                                netProfit,
                                netProfitMargin,
                                operatingMargin,
                                expenseRatio
                            },
                            timePeriod: {
                                startDate,
                                endDate
                            },
                            currencySymbol
                        })}
                        height={32}
                        className="evergreen-button"
                        style={{ backgroundColor: 'var(--maroon)', border: 'none' }}
                    >
                        Print Report
                    </Button>
                    <CSVLink
                        data={getCSVData()}
                        filename={csvFilename}
                        className="evergreen-button"
                    >
                        <Button
                            appearance="primary"
                            intent="success"
                            style={{ backgroundColor: 'var(--maroon)', border: 'none' }}
                        >
                            Download CSV
                        </Button>
                    </CSVLink>
                </Pane>
            </Card>


            <Pane display="flex" justifyContent="flex-end" marginBottom={50} marginTop={16} gap={8}></Pane>
        </>
    );
};

export default FinancialSummaryCard;
